import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import PageHeader from "Components/UI/PageHeader/PageHeader";
import TableActionTopbar, { ITableActionTopbarButton } from "Components/UI/TableActionTopbar";
import Table from "Components/UI/Table";
import { getColumnsTableTenants } from "./Tenants.columns";
import { AdmActions } from "Store/Adm/Adm.actions";
import TenantUpdate from "./Modals/TenantUpdate";
import { Skeleton } from "antd";
import { ITenantAdm } from "Data/interfaces/Tenant/ITenantAdm";
import { IUserStoreItemRequest } from "Data/interfaces/Store/IUserStoreItemRequest";
import TenantBillingData from "./Modals/TenantBillingData";
import TenantApprovePlanChange from "./Modals/TenantApprovePlanChange";
import TenantAdminChange from "./Modals/TenantAdminChange";
import TenantPersonalization from "./Modals/TenantPersonalization";
import useWindowSize from "Hooks/useWindowSize";
import styles from "./Tenants.module.scss";

const Tenants: FC<Props> = ({
  Tenants,
  tenantIsLoading,
  Planos,
  tenantUsers,
  adminChangeLoading,
  userInfo,
}) => {
  const dispatch = useDispatch();
  const windowSize = useWindowSize();

  const tableHeight = useMemo(() => {
    const reservedSpace = 260;
    const calculatedHeight = windowSize.height - reservedSpace;
    return Math.max(400, Math.min(calculatedHeight, 800));
  }, [windowSize.height]);

  const [selectedRowTenant, setSelectedRowTenant] = useState<number[]>([]);
  const [showCreateOrUpdateModalTenants, setShowCreateOrUpdateModalTenants] = useState(false);
  const [showBillingDataTenants, setShowBillingDataTenants] = useState(false);
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [showFilterCompanyTenants, setShowFilterCompanyTenants] = useState(false);
  const [showFilterEmailTenants, setShowFilterEmailTenants] = useState(false);
  const [showFilterNameTenants, setShowFilterNameTenants] = useState(false);
  const [tenant, setTenant] = useState<IUserStoreItemRequest>();
  const [tenantUpOrDown, setTenantUpOrDown] = useState<ITenantAdm[]>();
  const [tenantName, setTenantName] = useState<string>('');
  const [userAdminId, setUserAdminId] = useState<string>('');
  const [showTenantReviewPlanChange, setShowTenantReviewPlanChange] = useState(false);
  const [showTenantAdminChange, setShowTenantAdminChange] = useState(false);
  const [tenantAdmin, setTenantAdmin] = useState<ITenantAdm | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [tenantId, setTenantId] = useState<number>(0);
  const [billingTenant, setBillingTenant] = useState<ITenantAdm | null>(null);

  const sortTenantsList = useCallback((sortOrder: boolean, key: string, key2?: string) => {
    const sortedTenants = [
      ...(Tenants?.sort((a: any, b: any) => {
        let propA = a[key] || 0 || '';
        let propB = b[key] || 0 || '';

        if (key2) {
          propA = a[key][key2] || 0 || '';
          propB = b[key][key2] || 0 || '';
        }

        if ((typeof propA !== 'number' && typeof propB !== 'number') &&
          (typeof propA !== 'object' && typeof propB !== 'object') &&
          (propA.includes('/') || propB.includes('/'))) {
          const propAD = propA.split('/' || '-').reverse().join('');
          const propBD = propB.split('/' || '-').reverse().join('');
          return sortOrder ? Number(propBD) - Number(propAD) : Number(propAD) - Number(propBD);
        }

        if (typeof propA === 'number' && typeof propB === 'number') {
          return sortOrder ? propB - propA : propA - propB;
        }

        return propA.toString().localeCompare(propB.toString()) * (sortOrder ? -1 : 1);
      }) || [])
    ];

    dispatch(AdmActions.listTenantsUpdate(sortedTenants));
  }, [Tenants, dispatch]);

  const onChange = useCallback((selectedRowKeys: React.Key[], selectedRows: ITenantAdm[]) => {
    setSelectedRowTenant(selectedRowKeys as number[]);
    setTenantUpOrDown(selectedRows);
  }, []);

  const editTenant = useCallback((
    tenantId: number,
    tenantName: string,
  ) => {
    setTenantId(tenantId);
    setTenantName(tenantName);
    setShowCreateOrUpdateModalTenants(true);
  }, []);

  const editTenantAdmin = useCallback((tenant: ITenantAdm) => {
    if (tenant.Tenant.TenantId) {
      setTenantAdmin(tenant);
      dispatch(AdmActions.listTenantUsers(tenant.Tenant.TenantId));
      setShowTenantAdminChange(true);
    }
  }, [dispatch]);

  const editTenantPersonalization = useCallback((tenantId: number) => {
    dispatch(AdmActions.getTenantPersonalizationAdm(tenantId));
    setShowPersonalization(true);
  }, [dispatch]);

  const showBillingData = useCallback((tenant: ITenantAdm) => {
    setUserAdminId(tenant.UserAdminId);
    setTenantName(tenant.Tenant.CompanyName);
    // A aba de rateio da modal precisa do tenant e do preço do plano.
    setBillingTenant(tenant);
    setShowBillingDataTenants(true);
  }, []);

  const closeEditTenantModal = useCallback(() => {
    setShowCreateOrUpdateModalTenants(false);
    setShowBillingDataTenants(false);
    setShowTenantReviewPlanChange(false);
    setShowPersonalization(false);
  }, []);

  const closeEditTenantUpdateModal = useCallback(() => {
    setSelectedRowTenant([]);
    setTenantAdmin(null);
    setShowTenantAdminChange(false);
    setShowBillingDataTenants(false);
    setShowCreateOrUpdateModalTenants(false);
    setShowTenantReviewPlanChange(false);
    setShowPersonalization(false);
  }, []);

  const getActionToolbar = useCallback((): ITableActionTopbarButton[] => {
    return [
      {
        icon: 'atualizar',
        iconCustomSize: 16,
        description: 'Atualizar',
        hidden: false,
        action: () => {
          dispatch(AdmActions.listTenants(true));
        }
      },
      {
        icon: 'criativy',
        description: 'Gerenciamento Personalização',
        hidden: selectedRowTenant.length !== 1,
        action: () => {
          if (Tenants) {
            const index = selectedRowTenant[0];
            const tenant = Tenants[index];
            if (tenant?.Tenant?.TenantId) {
              editTenantPersonalization(tenant.Tenant.TenantId);
            }
          }
        }
      },
      {
        icon: 'menuGerenciarUsuario',
        description: 'Trocar admin do Tenant',
        hidden: selectedRowTenant.length !== 1,
        action: () => {
          if (Tenants) {
            const index = selectedRowTenant[0];
            const tenant = Tenants[index];
            editTenantAdmin(tenant);
          }
        }
      },
      {
        icon: 'menuContexto',
        description: 'Gerenciamento de Tenant',
        hidden: selectedRowTenant.length !== 1,
        action: () => {
          if (Tenants) {
            const index = selectedRowTenant[0];
            const tenant = Tenants[index];
            editTenant(tenant.Tenant.TenantId || 0, tenant.Tenant.CompanyName);
          }
        }
      },
      {
        icon: 'arquivoTxt',
        description: 'Dados de Faturamento',
        hidden: selectedRowTenant.length !== 1,
        action: () => {
          if (Tenants) {
            const index = selectedRowTenant[0];
            const tenant = Tenants[index];
            showBillingData(tenant);
          }
        }
      },
      {
        icon: 'atualizar',
        description: ((tenantUpOrDown && tenantUpOrDown.length > 0) &&
          (tenantUpOrDown[0].UserStoreItem.StorePlan && tenantUpOrDown[0].UserStoreItem.UpdateStorePlan) &&
          (tenantUpOrDown[0]?.UserStoreItem.UpdateStorePlan?.Order > tenantUpOrDown[0]?.UserStoreItem.StorePlan?.Order))
          ? 'Revisão de Upgrade' : 'Revisão de Downgrade',
        hidden: (selectedRowTenant.length !== 1) || (tenantUpOrDown && !tenantUpOrDown[0]?.UserStoreItem.UpdateStorePlan),
        action: () => {
          if (tenantUpOrDown?.length === 1) {
            setShowTenantReviewPlanChange(true);
          }
        }
      },
    ]
  }, [selectedRowTenant, Tenants, tenantUpOrDown, editTenantPersonalization, editTenantAdmin, editTenant, showBillingData, dispatch]);

  const rowSelection = {
    selectedRowKeys: selectedRowTenant,
    onChange
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: Tenants?.length || 0,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} de ${total} tenants`,
    pageSizeOptions: [100, 200, 500, 1000],
    onChange: (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    onShowSizeChange: (current: number, size: number) => {
      setCurrentPage(1);
      setPageSize(size);
    }
  };

  useEffect(() => {
    dispatch(AdmActions.listTenants());
    dispatch(AdmActions.listPlans());
  }, [dispatch]);

  return (
    <>
      <PageHeader
        name="Controle Administrativo de Tenants"
      />
      <div className={styles['tableWrapper']}>
        <TableActionTopbar buttons={getActionToolbar()} />
        {tenantIsLoading ? (
          <Skeleton active />
        ) : (
          <Table
            columns={getColumnsTableTenants({
              tenantsList: Tenants,
              plansList: Planos,
              sortTenantsList: sortTenantsList,
              editTenant: editTenant,
              showFilterCompanyTenants: showFilterCompanyTenants,
              setShowFilterCompanyTenants: setShowFilterCompanyTenants,
              showFilterEmailTenants: showFilterEmailTenants,
              setShowFilterEmailTenants: setShowFilterEmailTenants,
              showFilterNameTenants: showFilterNameTenants,
              setShowFilterNameTenants: setShowFilterNameTenants
            })}
            dataSource={Tenants?.map((tenant, index) => (
              { ...tenant, key: index }
            ))}
            rowSelection={rowSelection}
            pagination={paginationConfig}
          />
        )}
      </div>

      {showPersonalization && (
        <TenantPersonalization
          visible={showPersonalization}
          onClose={closeEditTenantModal}
          closeEditTenantPersonalizationModal={closeEditTenantUpdateModal}
        />
      )}
      {showCreateOrUpdateModalTenants && (
        <TenantUpdate
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={closeEditTenantModal}
          visible={showCreateOrUpdateModalTenants}
          closeEditTenantUpdateModal={closeEditTenantUpdateModal}
        />
      )}
      {showBillingDataTenants && (
        <TenantBillingData
          UserAdminId={userAdminId}
          tenantId={billingTenant?.Tenant.TenantId || 0}
          tenantName={tenantName}
          planPrice={
            billingTenant?.UserStoreItem?.Price ||
            billingTenant?.UserStoreItem?.StorePlan?.Price
          }
          onClose={closeEditTenantModal}
          visible={showBillingDataTenants}
          closeEditTenantUpdateModal={closeEditTenantUpdateModal}
        />
      )}
      {showTenantReviewPlanChange && (
        <TenantApprovePlanChange
          tenant={
            (tenantUpOrDown && tenantUpOrDown.length === 1)
              ? tenantUpOrDown[0]?.UserStoreItem : undefined
          }
          tenantId={
            (tenantUpOrDown && tenantUpOrDown.length === 1)
              ? tenantUpOrDown[0]?.Tenant.TenantId : undefined
          }
          userInfo={userInfo}
          onClose={closeEditTenantModal}
          visible={showTenantReviewPlanChange}
          closeEditTenantUpdateModal={closeEditTenantUpdateModal}
        />
      )}
      {showTenantAdminChange && (
        <TenantAdminChange
          onCancel={closeEditTenantUpdateModal}
          visible={showTenantAdminChange}
          tenant={tenantAdmin}
          tenantUsers={tenantUsers}
          dispatch={dispatch}
          loading={adminChangeLoading}
        />
      )}
    </>
  )
}

const mapState = (state: IGlobalReducerState) => ({
  ...state.adm,
  userInfo: state.auth.userInfo,
})

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux;

export default connector(Tenants);
